/**
 * The casino ROOM — so nothing floats. A large carpet floor + four wainscot walls
 * + a coffered ceiling band, wrapping the D-shape table in a believable interior.
 *
 * Textures (carpet + wall) are produced by the asset agent under
 * `public/assets/textures/{carpet,wall}/`. Every load uses the SUSPENDING
 * `useSuspendingTexture` (see guarded.ts) so the <Suspense> in Scene.tsx waits
 * until the textures exist and each material is created WITH its maps present on
 * the first render — identical on cold and warm loads. A genuinely-absent file
 * resolves to `null`, falling back to a palette solid (deep-crimson carpet,
 * warm-walnut wall). The scene is lit purely by explicit lights (Lighting.tsx);
 * the room grounds it in real geometry.
 *
 * Draw-call budget: floor(1) + 4 walls(4) + ceiling(1) + skirting trim(1) ≈ 7.
 */

import type * as THREE from 'three';
import { palette, assetPaths, materials } from '../theme/tokens';
import { useSuspendingTexture } from '../assets/guarded';
import { FLAT_Z, TABLE } from './seating';

/** Room footprint (world units). The table center sits near the room center-ish. */
const ROOM = {
  halfX: 9,
  halfZ: 9,
  // Tall enough that the back wall fills the frame ABOVE the sign at the new
  // lower/behind-player camera (camera y ~5.6 sat ABOVE the old 5m wall top, so
  // you saw over it into the void). 8m keeps the wall behind the whole frustum.
  height: 8,
  /** Floor y. */
  floorY: 0,
  /** Where the back wall (behind the dealer) sits — close enough to read the sign. */
  backZ: FLAT_Z - 5.5,
  frontZ: FLAT_Z - 5.5 + 18,
} as const;

/** Coerce the suspending hook's `null` (absent file) → `undefined` (use fallback). */
function orUndef(tex: THREE.Texture | null): THREE.Texture | undefined {
  return tex ?? undefined;
}

export function Room() {
  // All room textures load via the SUSPENDING hook (see guarded.ts): the <Suspense>
  // in Scene.tsx waits until they exist, so every material below is created WITH its
  // maps present on the first render — no post-compile `needsUpdate` hazard, identical
  // on cold and warm loads. Repeat is baked in by the hook before first render.
  // Carpet (floor).
  const carpet = orUndef(useSuspendingTexture(assetPaths.carpetTexture, { kind: 'color', repeat: [8, 8] }));
  const carpetN = orUndef(useSuspendingTexture(assetPaths.carpetNormal, { kind: 'linear', repeat: [8, 8] }));
  const carpetR = orUndef(useSuspendingTexture(assetPaths.carpetRoughness, { kind: 'linear', repeat: [8, 8] }));
  // Wall (wainscot).
  const wall = orUndef(useSuspendingTexture(assetPaths.wallTexture, { kind: 'color', repeat: [6, 5] }));
  const wallN = orUndef(useSuspendingTexture(assetPaths.wallNormal, { kind: 'linear', repeat: [6, 5] }));
  const wallR = orUndef(useSuspendingTexture(assetPaths.wallRoughness, { kind: 'linear', repeat: [6, 5] }));

  const wallY = ROOM.height / 2;
  const cz = FLAT_Z + TABLE.radiusZ * 0.5;

  return (
    <group>
      {/* ---- Carpet floor ---- */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, ROOM.floorY, cz]} receiveShadow>
        <planeGeometry args={[ROOM.halfX * 2, ROOM.halfZ * 2]} />
        <meshStandardMaterial
          color={carpet ? '#ffffff' : palette.carpet}
          roughness={0.95}
          metalness={0}
          {...(carpet ? { map: carpet } : {})}
          {...(carpetN ? { normalMap: carpetN } : {})}
          {...(carpetR ? { roughnessMap: carpetR } : {})}
        />
      </mesh>

      {/* ---- Walls (4) ---- */}
      {/* Back wall (behind the dealer; the High Rollers sign mounts here). */}
      <mesh position={[0, wallY, ROOM.backZ]} receiveShadow>
        <planeGeometry args={[ROOM.halfX * 2, ROOM.height]} />
        <WallMaterial map={wall} normalMap={wallN} roughnessMap={wallR} />
      </mesh>
      {/* Front wall (behind the camera; faces -z). */}
      <mesh position={[0, wallY, ROOM.frontZ]} rotation={[0, Math.PI, 0]} receiveShadow>
        <planeGeometry args={[ROOM.halfX * 2, ROOM.height]} />
        <WallMaterial map={wall} normalMap={wallN} roughnessMap={wallR} />
      </mesh>
      {/* Left wall. */}
      <mesh position={[-ROOM.halfX, wallY, cz]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
        <planeGeometry args={[ROOM.halfZ * 2, ROOM.height]} />
        <WallMaterial map={wall} normalMap={wallN} roughnessMap={wallR} />
      </mesh>
      {/* Right wall. */}
      <mesh position={[ROOM.halfX, wallY, cz]} rotation={[0, -Math.PI / 2, 0]} receiveShadow>
        <planeGeometry args={[ROOM.halfZ * 2, ROOM.height]} />
        <WallMaterial map={wall} normalMap={wallN} roughnessMap={wallR} />
      </mesh>

      {/* ---- Ceiling (dim warm slab so reflections don't show void above) ---- */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, ROOM.height, cz]}>
        <planeGeometry args={[ROOM.halfX * 2, ROOM.halfZ * 2]} />
        <meshStandardMaterial color={palette.backdrop} roughness={1} metalness={0} />
      </mesh>

      {/* ---- Gold skirting band where the back wall meets the floor (blooms subtly) ---- */}
      <mesh position={[0, 0.06, ROOM.backZ + 0.05]}>
        <boxGeometry args={[ROOM.halfX * 2, 0.12, 0.06]} />
        <meshStandardMaterial {...materials.brass} />
      </mesh>
    </group>
  );
}

/** Shared wall material; palette walnut fallback when the texture is absent. */
function WallMaterial({
  map,
  normalMap,
  roughnessMap,
}: {
  readonly map: THREE.Texture | undefined;
  readonly normalMap: THREE.Texture | undefined;
  readonly roughnessMap: THREE.Texture | undefined;
}) {
  return (
    <meshStandardMaterial
      color={map ? '#ffffff' : palette.wall}
      roughness={0.85}
      metalness={0.05}
      {...(map ? { map } : {})}
      {...(normalMap ? { normalMap } : {})}
      {...(roughnessMap ? { roughnessMap } : {})}
    />
  );
}
